import { useEffect, useMemo, useState } from 'react'
import { useInRouterContext, useSearchParams, type SetURLSearchParams } from 'react-router-dom'
import { ChevronDown, Loader2, SlidersHorizontal, Star, X } from 'lucide-react'
import { useReferenceStore } from '@/storefront/context/ReferenceStoreContext'
import { useReferenceCategories } from '@/storefront/hooks/useReferenceCatalog'
import { readInStockOnlyFromSearchParams, stripLegacyShopFilterParams } from '@/storefront/lib/shopUrlQueryParams'

const EMPTY_SEARCH_PARAMS = new URLSearchParams()

const noopSetSearchParams: SetURLSearchParams = () => {}

function parseMoney(s: string | null): number | null {
  if (s == null || s.trim() === '') return null
  const n = Number(s)
  return Number.isFinite(n) && n >= 0 ? n : null
}

function parseRating(s: string | null): number | null {
  if (s == null || s.trim() === '') return null
  const n = parseInt(s, 10)
  if (!Number.isFinite(n) || n < 1 || n > 5) return null
  return n
}

export type FiltersSidebarBlockProps = {
  enablePrice?: boolean
  enableCategory?: boolean
  enableRating?: boolean
}

function FiltersSidebarRouted(props: FiltersSidebarBlockProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  return (
    <FiltersSidebarContent
      {...props}
      searchParams={searchParams}
      setSearchParams={setSearchParams}
    />
  )
}

function FiltersSidebarContent(
  props: FiltersSidebarBlockProps & {
    searchParams: URLSearchParams
    setSearchParams: SetURLSearchParams
  }
) {
  const enablePrice = props.enablePrice !== false
  const enableCategory = props.enableCategory !== false
  const enableRating = props.enableRating !== false
  const { store } = useReferenceStore()
  const catsQ = useReferenceCategories(store.id)
  const { searchParams, setSearchParams } = props
  const [mobileOpen, setMobileOpen] = useState(false)

  const categories = catsQ.data ?? []

  const categoryId = searchParams.get('category_id')
  const validCategoryId =
    categoryId && categories.some((c) => c.id === categoryId) ? categoryId : ''

  const minPrice = parseMoney(searchParams.get('min_price'))
  const maxPrice =
    parseMoney(searchParams.get('max_price')) ?? parseMoney(searchParams.get('maxPrice'))
  const minRating = parseRating(searchParams.get('min_rating'))

  const [draftMin, setDraftMin] = useState(() => (minPrice != null ? String(minPrice) : ''))
  const [draftMax, setDraftMax] = useState(() => (maxPrice != null ? String(maxPrice) : ''))

  useEffect(() => {
    setDraftMin(minPrice != null ? String(minPrice) : '')
    setDraftMax(maxPrice != null ? String(maxPrice) : '')
  }, [minPrice, maxPrice])

  const activeCount = useMemo(() => {
    let n = 0
    if (validCategoryId) n += 1
    if (minPrice != null || maxPrice != null) n += 1
    if (minRating != null) n += 1
    const sort = searchParams.get('sort')
    if (sort && sort !== 'featured') n += 1
    if (searchParams.get('view') === 'list') n += 1
    if (readInStockOnlyFromSearchParams(searchParams)) n += 1
    return n
  }, [validCategoryId, minPrice, maxPrice, minRating, searchParams])

  const patchParams = (mutate: (p: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        mutate(next)
        return next
      },
      { replace: true }
    )
  }

  const clearAll = () => {
    patchParams((p) => {
      p.delete('category_id')
      p.delete('min_price')
      p.delete('max_price')
      p.delete('min_rating')
      p.delete('sort')
      p.delete('view')
      p.delete('in_stock')
      stripLegacyShopFilterParams(p)
    })
    setDraftMin('')
    setDraftMax('')
    setMobileOpen(false)
  }

  const applyPrice = () => {
    const min = draftMin.trim() === '' ? null : Number(draftMin)
    const max = draftMax.trim() === '' ? null : Number(draftMax)
    patchParams((p) => {
      p.delete('min_price')
      p.delete('max_price')
      if (min != null && Number.isFinite(min) && min >= 0) p.set('min_price', String(min))
      if (max != null && Number.isFinite(max) && max >= 0) p.set('max_price', String(max))
    })
    setMobileOpen(false)
  }

  const setCategory = (id: string | null) => {
    patchParams((p) => {
      if (id && categories.some((c) => c.id === id)) p.set('category_id', id)
      else p.delete('category_id')
    })
    setMobileOpen(false)
  }

  const setStars = (stars: number | null) => {
    patchParams((p) => {
      if (stars != null && stars >= 1 && stars <= 5) p.set('min_rating', String(stars))
      else p.delete('min_rating')
    })
    setMobileOpen(false)
  }

  if (catsQ.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 border-b border-border/80 bg-ref-bg px-4 py-8 sm:px-6 lg:px-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
        <span className="text-sm font-medium text-muted-foreground">Chargement des filtres…</span>
      </div>
    )
  }

  if (catsQ.isError) {
    return (
      <div className="border-b border-border/80 bg-ref-bg px-4 py-8 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold text-red-800">Impossible de charger les catégories</p>
        <button type="button" onClick={() => catsQ.refetch()} className="mt-2 text-xs font-semibold text-red-700 underline-offset-2 hover:underline">
          Réessayer
        </button>
      </div>
    )
  }

  const filterBody = (
    <div className="space-y-8">
      {enableCategory ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Catégorie</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategory(null)}
              className={[
                'rounded-full border px-3 py-1.5 text-sm font-medium transition',
                !validCategoryId ? 'border-ref-primary bg-ref-primary/10 text-ref-text' : 'border-border bg-card text-foreground hover:border-border',
              ].join(' ')}
            >
              Tout
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                className={[
                  'rounded-full border px-3 py-1.5 text-sm font-medium transition',
                  validCategoryId === c.id ? 'border-ref-primary bg-ref-primary/10 text-ref-text' : 'border-border bg-card text-foreground hover:border-border',
                ].join(' ')}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {enablePrice ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Prix (TND)</p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[120px] flex-1 flex-col gap-1 text-xs text-muted-foreground">
              Min.
              <input
                value={draftMin}
                onChange={(e) => setDraftMin(e.target.value)}
                inputMode="decimal"
                className="rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm outline-none ring-ref-primary/30 focus:ring-2"
                placeholder="0"
              />
            </label>
            <label className="flex min-w-[120px] flex-1 flex-col gap-1 text-xs text-muted-foreground">
              Max.
              <input
                value={draftMax}
                onChange={(e) => setDraftMax(e.target.value)}
                inputMode="decimal"
                className="rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm outline-none ring-ref-primary/30 focus:ring-2"
                placeholder="Sans limite"
              />
            </label>
            <button
              type="button"
              onClick={applyPrice}
              className="inline-flex h-10 items-center rounded-xl bg-ref-primary px-4 text-sm font-semibold text-white shadow-sm transition hover:opacity-95"
            >
              Appliquer
            </button>
          </div>
        </div>
      ) : null}

      {enableRating ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Popularité</p>
          <p className="mt-1 text-xs text-muted-foreground">Filtre basé sur les commandes répétées — met en avant les pièces les plus demandées.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setStars(null)}
              className={[
                'rounded-full border px-3 py-1.5 text-sm font-medium transition',
                minRating == null ? 'border-ref-primary bg-ref-primary/10 text-ref-text' : 'border-border bg-card text-foreground hover:border-border',
              ].join(' ')}
            >
              Tous
            </button>
            {[2, 3, 4, 5].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStars(s)}
                className={[
                  'inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm font-medium transition',
                  minRating === s ? 'border-ref-primary bg-ref-primary/10 text-ref-text' : 'border-border bg-card text-foreground hover:border-border',
                ].join(' ')}
              >
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden />
                {s}+
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {activeCount > 0 ? (
        <button
          type="button"
          onClick={clearAll}
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          <X className="h-4 w-4" aria-hidden />
          Réinitialiser les filtres
        </button>
      ) : null}
    </div>
  )

  return (
    <section className="border-b border-border/80 bg-ref-bg">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <div className="lg:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="flex w-full items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 text-left shadow-sm"
          >
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground" aria-hidden />
              Filtres
              {activeCount > 0 ? (
                <span className="rounded-full bg-ref-primary px-2 py-0.5 text-[11px] font-bold text-white">{activeCount}</span>
              ) : null}
            </span>
            <ChevronDown className={`h-5 w-5 text-muted-foreground transition ${mobileOpen ? 'rotate-180' : ''}`} aria-hidden />
          </button>
          {mobileOpen ? <div className="mt-4 rounded-2xl border border-border bg-card p-5 shadow-sm">{filterBody}</div> : null}
        </div>

        <div className="hidden lg:block">
          <aside className="w-72 shrink-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Boutique</p>
            <h2 className="mt-2 text-lg font-bold tracking-tight text-ref-text">Affiner les résultats</h2>
            <div className="mt-6">{filterBody}</div>
          </aside>
        </div>
      </div>
    </section>
  )
}

export function FiltersSidebarBlock(props: FiltersSidebarBlockProps) {
  const inRouter = useInRouterContext()
  if (!inRouter) {
    return (
      <FiltersSidebarContent
        {...props}
        searchParams={EMPTY_SEARCH_PARAMS}
        setSearchParams={noopSetSearchParams}
      />
    )
  }
  return <FiltersSidebarRouted {...props} />
}
